/**
 * Latori: reads the Rivo loyalty program configuration from the storefront
 * so the theme's points displays follow whatever the merchant sets in the
 * Rivo admin instead of hardcoded constants.
 *
 * Rivo's app embed (rivo-app-embed.js) exposes `window.Rivo.loy_config`
 * (confirmed by Rivo support, 2026-09-23). The parts used here:
 *   - loy_config.ways_to_earn: the "Place an order" rule (trigger
 *     `order_placed`) carries `balance_amount` per `currency_base_amount`
 *     and, per VIP tier, `multi_balance_settings_by_tiers[<Tier name>]`.
 *   - loy_config.order_settings: `points_order_earnings_rounding_*` — the
 *     one rounding step Rivo applies at the very end (floor / round / ceil,
 *     with a precision).
 *   - RivoAPI.cart_earnings_estimation(): POSTs `Rivo.common.cart` for the
 *     logged-in customer and returns the points Rivo would award, including
 *     excluded products/collections/tags. Only for logged-in customers.
 *
 * The embed loads deferred and independently of the theme's scripts, so
 * nothing here assumes it is present: `ready()` polls for it, and every
 * getter returns null while it isn't — callers keep their Liquid-rendered
 * fallback (the same hardcoded table as before) in that case.
 *
 * Classic script, no modules (see AGENTS.md §12).
 */
(function () {
  if (window.rivoConfigLatori) return;

  var POLL_MS = 100;
  var POLL_MAX_MS = 15000;
  var waiters = [];
  var polling = false;

  function config() {
    var rivo = window.Rivo;
    return rivo && rivo.loy_config && typeof rivo.loy_config === 'object' ? rivo.loy_config : null;
  }

  function startPolling() {
    if (polling) return;
    polling = true;
    var waited = 0;
    var timer = setInterval(function () {
      waited += POLL_MS;
      if (config() || waited >= POLL_MAX_MS) {
        clearInterval(timer);
        polling = false;
        var cbs = waiters;
        waiters = [];
        cbs.forEach(function (cb) {
          try {
            cb(config());
          } catch (e) {
            console.error('[rivo-config-latori]', e);
          }
        });
      }
    }, POLL_MS);
  }

  /** Runs cb(config|null) once Rivo's config is available (or the wait timed out). */
  function ready(cb) {
    if (config()) {
      cb(config());
      return;
    }
    waiters.push(cb);
    startPolling();
  }

  /** The active "Place an order" earn rule, or null. */
  function orderRule() {
    var c = config();
    if (!c || !Array.isArray(c.ways_to_earn)) return null;
    for (var i = 0; i < c.ways_to_earn.length; i++) {
      var w = c.ways_to_earn[i];
      if (w && w.trigger === 'order_placed' && w.status === 'active') return w;
    }
    return null;
  }

  /**
   * Points rate for a tier key as the theme uses it ('bronze'|'silver'|'gold',
   * compared case-insensitively against Rivo's tier names). Falls back to the
   * rule's base rate when the tier has no override. Null without config.
   * @returns {{balanceAmount: number, currencyBaseAmount: number}|null}
   */
  function rate(tierKey) {
    var rule = orderRule();
    if (!rule) return null;
    var byTier = rule.multi_balance_settings_by_tiers || {};
    var wanted = String(tierKey || '').toLowerCase();
    var settings = null;
    Object.keys(byTier).forEach(function (name) {
      if (name.toLowerCase() === wanted) settings = byTier[name];
    });
    var balance = settings && settings.balance_amount != null ? settings.balance_amount : rule.balance_amount;
    var base = settings && settings.currency_base_amount != null ? settings.currency_base_amount : rule.currency_base_amount;
    balance = parseFloat(balance);
    base = parseFloat(base);
    if (isNaN(balance) || isNaN(base) || base <= 0) return null;
    return { balanceAmount: balance, currencyBaseAmount: base };
  }

  /** Rivo's single, final rounding step. Falls back to floor to whole points. */
  function round(rawPoints) {
    var c = config();
    var s = (c && c.order_settings) || {};
    var enabled = s.points_order_earnings_rounding_enabled !== false;
    var precision = parseInt(s.points_order_earnings_rounding_precision, 10);
    if (isNaN(precision)) precision = 0;
    var type = s.points_order_earnings_rounding_type || 'floor';
    // Rivo rounds to 2 decimals first, then applies the shop rule once.
    var value = Math.round(rawPoints * 100) / 100;
    if (!enabled) return value;
    var factor = Math.pow(10, precision);
    var fn = type === 'ceil' ? Math.ceil : type === 'round' ? Math.round : Math.floor;
    return fn(value * factor) / factor;
  }

  /**
   * Points for an amount in cents at a tier, using Rivo's config. Null while
   * the config isn't available, so the caller can keep its fallback.
   */
  function points(amountCents, tierKey) {
    var r = rate(tierKey);
    if (!r) return null;
    var amount = (parseInt(amountCents, 10) || 0) / 100;
    return round((amount / r.currencyBaseAmount) * r.balanceAmount);
  }

  /**
   * Asks Rivo for the points the current cart would earn the logged-in
   * customer. Resolves to a number, or null when unavailable (guest, API
   * missing, request failed). `cart` is the /cart.js payload to estimate;
   * RivoAPI reads `Rivo.common.cart`, which is the cart at page render, so
   * it is swapped in for the call and restored afterwards.
   */
  function estimateCart(cart) {
    var api = window.RivoAPI;
    var rivo = window.Rivo;
    if (!api || typeof api.cart_earnings_estimation !== 'function' || !rivo || !rivo.common) return Promise.resolve(null);
    var customer = rivo.common.customer;
    if (!customer || !customer.id) return Promise.resolve(null);
    if (!cart || !cart.item_count) return Promise.resolve(0);
    var previous = rivo.common.cart;
    rivo.common.cart = cart;
    var request;
    try {
      request = Promise.resolve(api.cart_earnings_estimation());
    } catch (e) {
      rivo.common.cart = previous;
      return Promise.resolve(null);
    }
    return request
      .then(function (res) {
        if (!res || res.success !== true || res.earnings_type !== 'points') return null;
        var n = parseFloat(res.earnings_amount);
        return isNaN(n) ? null : n;
      })
      .catch(function () {
        return null;
      })
      .then(function (result) {
        rivo.common.cart = previous;
        return result;
      });
  }

  /**
   * Points as shown to the customer: whole number, dot as thousands
   * separator ("1.450") - same as snippets/points-format-latori.liquid.
   */
  function formatPoints(value) {
    var n = Math.floor(Number(value) || 0);
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  window.rivoConfigLatori = {
    ready: ready,
    config: config,
    orderRule: orderRule,
    rate: rate,
    round: round,
    points: points,
    estimateCart: estimateCart,
    formatPoints: formatPoints
  };
})();
